import {
  Button,
  TextField,
  ActionBar,
  ErrorBanner,
  Modal,
} from "frontend-kit/components/index.js";
import { getChainApp } from "./chain-app.js";
import { duskToLux, luxToDusk } from "./chain-config.js";
import { loadTradingKey } from "./trading-key-store.js";
import { isParticipantReady, loadParticipant } from "./participant-store.js";
import {
  OP,
  encodeTradePayload,
  signTraderAuth,
  buildTraderAuthArg,
  hexToBytes,
} from "./trader-crypto.js";
import { showGlobalTx, trackTx } from "./tx-status.js";
import { withNonceRetry } from "./trader-nonce.js";
import { isAutoRetryEnabled } from "./trade-settings.js";
import { duskAmountNode } from "./dusk-amount.js";
export {
  spotPrices,
  buySharesOut,
  sellCollateralOut,
} from "./fpmm-math.js";
import {
  spotPrices,
  buySharesOut,
  sellCollateralOut,
  applyBuyReserves,
  applySellReserves,
  priceImpactBps,
  avgEffectivePrice,
} from "./fpmm-math.js";
import { TRADE_PRIVACY_BLURB } from "./privacy-copy.js";

const TIP_GAS =
  "Trade txs do not pull DUSK from your wallet - only gas. Deposit collateral first; buys debit that balance inside the contract.";
const TIP_AMOUNT =
  "You cannot buy the entire YES pool in one fill (FPMM leaves liquidity). Wallet confirm shows gas only - spend comes from free collateral.";

function formatPriceCents(p) {
  if (p == null || Number.isNaN(p)) return "-";
  return `${(Math.round(p * 1000) / 10).toFixed(1)}¢`;
}

function formatDusk(lux) {
  return duskAmountNode(lux);
}

/** Plain-string DUSK amount for contexts that can't hold a DOM node: thrown
 * Error messages and the wallet extension's opaque approval-UI metadata. */
function formatDuskText(lux) {
  return `${luxToDusk(lux)} DUSK`;
}

function tipBtn(text) {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "tip-btn";
  b.textContent = "?";
  b.title = text;
  b.setAttribute("aria-label", text);
  b.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    const existing = b.parentElement?.querySelector(".tip-pop");
    if (existing) {
      existing.remove();
      return;
    }
    document.querySelectorAll(".tip-pop").forEach((el) => el.remove());
    const pop = document.createElement("div");
    pop.className = "tip-pop";
    pop.setAttribute("role", "tooltip");
    pop.textContent = text;
    const host = b.parentElement;
    if (host) {
      if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
      }
      host.append(pop);
    }
    const close = (ev) => {
      if (ev.target === b || pop.contains(ev.target)) return;
      pop.remove();
      document.removeEventListener("click", close);
    };
    setTimeout(() => document.addEventListener("click", close), 0);
  });
  return b;
}

/**
 * Compact trade ticket. Position lives outside (left column).
 */
export function renderTradeTicket({
  marketId,
  log,
  onDone,
  onCollateralChange,
  yesPercent = null,
  yesReserve = null,
  noReserve = null,
  marketStatus = "open",
  closeBlock = null,
  blockHeight = null,
}) {
  const root = document.createElement("div");
  root.className = "trade-form trade-form-compact dt-card detail-trade-card";

  let side = "buy";
  let outcome = 0;
  /** @type {"dusk" | "lux"} */
  let amountUnit = "dusk";
  let freeCollateralLux = null;
  /** @type {[bigint, bigint] | null} shares held, [yes, no] */
  let positionShares = null;
  let busy = false;

  const head = document.createElement("div");
  head.className = "trade-head";
  const heading = document.createElement("strong");
  heading.textContent = "Trade";
  head.append(heading, tipBtn(TIP_GAS));
  root.append(head);

  function computeSpotPct() {
    const { pYes, pNo } = spotPrices(yesReserve, noReserve);
    return yesPercent != null
      ? { pYes: yesPercent / 100, pNo: 1 - yesPercent / 100 }
      : { pYes, pNo };
  }
  let spotPct = computeSpotPct();

  function computeClosed() {
    return (
      marketStatus !== "open" ||
      (closeBlock != null && blockHeight != null && blockHeight >= Number(closeBlock))
    );
  }
  let closed = computeClosed();

  const closedBannerHost = document.createElement("div");
  root.append(closedBannerHost);
  function paintClosedBanner() {
    closedBannerHost.replaceChildren();
    if (!closed) return;
    closedBannerHost.append(
      ErrorBanner({
        message:
          marketStatus !== "open"
            ? `Trading closed (${marketStatus}).`
            : "Trading closed - past close block.",
      })
    );
  }
  paintClosedBanner();

  const sideRow = document.createElement("div");
  sideRow.className = "trade-toggle-row";
  root.append(sideRow);

  const outcomeRow = document.createElement("div");
  outcomeRow.className = "trade-outcome-row";
  root.append(outcomeRow);

  const amountBlock = document.createElement("div");
  amountBlock.className = "trade-amount-block";
  root.append(amountBlock);

  const quoteEl = document.createElement("div");
  quoteEl.className = "trade-quote trade-quote-preview";
  root.append(quoteEl);

  const actionsHost = document.createElement("div");
  root.append(actionsHost);

  let amountInput = null;

  async function refreshCollateral() {
    try {
      const key = loadTradingKey();
      if (!key?.commitment_hex) {
        freeCollateralLux = null;
        paintAmount();
        onCollateralChange?.(null);
        return;
      }
      const dusk = await getChainApp();
      const bal = await dusk.readContract({
        contract: "predictionMarket",
        functionName: "collateral_of",
        args: Array.from(hexToBytes(key.commitment_hex)),
      });
      freeCollateralLux = BigInt(bal);
      paintAmount();
      onCollateralChange?.(freeCollateralLux);
    } catch {
      freeCollateralLux = null;
      paintAmount();
    }
  }

  async function refreshPosition() {
    try {
      const key = loadTradingKey();
      if (!key?.commitment_hex) {
        positionShares = null;
        paintAmount();
        return;
      }
      const dusk = await getChainApp();
      const pos = await dusk.readContract({
        contract: "predictionMarket",
        functionName: "position_of",
        args: [marketId, Array.from(hexToBytes(key.commitment_hex))],
      });
      positionShares = [BigInt(pos?.[0] ?? 0), BigInt(pos?.[1] ?? 0)];
      paintAmount();
    } catch {
      positionShares = null;
      paintAmount();
    }
  }

  function formatFreeDisplay(lux) {
    if (lux == null) return "-";
    return amountUnit === "lux" ? String(lux) : String(luxToDusk(lux));
  }

  function amountToInputValue(lux) {
    if (lux == null || lux <= 0n) return amountUnit === "lux" ? "0" : "0";
    return amountUnit === "lux" ? String(lux) : String(luxToDusk(lux));
  }

  function collateralLuxFromInput() {
    const raw = Number(amountInput?.value);
    if (!raw || raw <= 0 || Number.isNaN(raw)) return null;
    if (amountUnit === "lux") return BigInt(Math.round(raw));
    return duskToLux(raw);
  }

  function sharesFromInput() {
    const raw = Number(amountInput?.value);
    if (!raw || raw <= 0 || Number.isNaN(raw)) return null;
    return BigInt(Math.round(raw));
  }

  function switchUnit(next) {
    if (next === amountUnit) return;
    const lux = collateralLuxFromInput();
    amountUnit = next;
    paintAmount();
    if (lux != null && amountInput) {
      amountInput.value = amountToInputValue(lux);
    }
    updateQuote();
  }

  function paintAmount() {
    const prev = amountInput?.value;
    amountBlock.replaceChildren();
    const isBuy = side === "buy";

    const amountRow = document.createElement("div");
    amountRow.className = "trade-amount-row";

    const field = TextField({
      id: "trade-amount",
      label: isBuy ? "Amount" : `Shares (${outcome === 0 ? "YES" : "NO"})`,
      value: prev ?? (isBuy ? (amountUnit === "lux" ? "100000000" : "0.1") : "1"),
      onInput: () => updateQuote(),
    });
    const input = field.querySelector("input");
    if (input) {
      input.title = isBuy ? TIP_AMOUNT : "Sell returns collateral from the pool.";
      if (closed) input.disabled = true;
    }
    amountInput = input;
    const labelEl = field.querySelector("label");
    if (labelEl && isBuy) {
      labelEl.append(tipBtn(TIP_AMOUNT));
      labelEl.classList.add("trade-label-with-tip");
    }
    amountRow.append(field);

    if (isBuy) {
      const unit = document.createElement("div");
      unit.className = "unit-toggle";
      unit.setAttribute("role", "group");
      unit.setAttribute("aria-label", "Amount unit");
      for (const u of ["dusk", "lux"]) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `unit-toggle-btn${amountUnit === u ? " is-active" : ""}`;
        btn.textContent = u.toUpperCase();
        btn.disabled = closed;
        btn.addEventListener("click", () => switchUnit(u));
        unit.append(btn);
      }
      amountRow.append(unit);
    }
    amountBlock.append(amountRow);

    if (isBuy) {
      const aux = document.createElement("div");
      aux.className = "trade-amount-aux";
      const freeField = TextField({
        id: "trade-free",
        label: `Free (${amountUnit.toUpperCase()})`,
        value: formatFreeDisplay(freeCollateralLux),
      });
      const freeInput = freeField.querySelector("input");
      if (freeInput) {
        freeInput.readOnly = true;
        freeInput.disabled = true;
        freeInput.tabIndex = -1;
        freeInput.title = "Free collateral available to spend on buys";
        freeInput.classList.add("is-readonly");
        freeInput.setAttribute("aria-readonly", "true");
      }
      freeField.classList.add("funds-free-field");
      aux.append(freeField);
      aux.append(
        Button({
          label: "Max",
          disabled: closed || freeCollateralLux == null || freeCollateralLux <= 0n,
          onClick: () => {
            if (freeCollateralLux == null || freeCollateralLux <= 0n) return;
            amountInput.value = amountToInputValue(freeCollateralLux);
            updateQuote();
          },
        })
      );
      amountBlock.append(aux);
    } else {
      const heldShares = positionShares ? positionShares[outcome] : null;
      const aux = document.createElement("div");
      aux.className = "trade-amount-aux";
      const heldField = TextField({
        id: "trade-held",
        label: `Held (${outcome === 0 ? "YES" : "NO"})`,
        value: heldShares != null ? heldShares.toString() : "-",
      });
      const heldInput = heldField.querySelector("input");
      if (heldInput) {
        heldInput.readOnly = true;
        heldInput.disabled = true;
        heldInput.tabIndex = -1;
        heldInput.title = "Shares you currently hold for this outcome";
        heldInput.classList.add("is-readonly");
        heldInput.setAttribute("aria-readonly", "true");
      }
      heldField.classList.add("funds-free-field");
      aux.append(heldField);
      aux.append(
        Button({
          label: "Max",
          disabled: closed || heldShares == null || heldShares <= 0n,
          onClick: () => {
            if (heldShares == null || heldShares <= 0n) return;
            amountInput.value = heldShares.toString();
            updateQuote();
          },
        })
      );
      amountBlock.append(aux);
    }
  }

  function updateQuote() {
    quoteEl.replaceChildren();
    quoteEl.className = "trade-quote trade-quote-preview";
    const outcomeLabel = outcome === 0 ? "YES" : "NO";
    const tone = outcome === 0 ? "is-yes" : "is-no";

    function setMuted(text) {
      quoteEl.classList.add("is-muted");
      quoteEl.textContent = text;
    }
    function setWarn(...parts) {
      quoteEl.classList.add("is-warn");
      quoteEl.append(...parts);
    }
    function addQuoteRow(label, valueNode, rowTone) {
      const row = document.createElement("div");
      row.className = "trade-quote-row";
      if (rowTone) row.classList.add(rowTone);
      const lab = document.createElement("span");
      lab.textContent = label;
      const val = document.createElement("strong");
      if (typeof valueNode === "string") val.textContent = valueNode;
      else val.append(valueNode);
      row.append(lab, val);
      return row;
    }
    function setQuoteGrid(primaryLabel, primaryValue, rows) {
      quoteEl.classList.add(tone);
      const main = document.createElement("div");
      main.className = "trade-quote-primary";
      main.append(primaryValue);
      const sub = document.createElement("div");
      sub.className = "trade-quote-secondary";
      sub.textContent = primaryLabel;
      quoteEl.append(main, sub);
      const grid = document.createElement("div");
      grid.className = "trade-quote-rows";
      for (const r of rows) grid.append(r);
      quoteEl.append(grid);
    }

    if (yesReserve == null || noReserve == null) {
      setMuted("Reserves unavailable");
      return;
    }
    if (side === "buy") {
      const lux = collateralLuxFromInput();
      if (lux == null) {
        setMuted("Enter an amount");
        return;
      }
      if (freeCollateralLux != null && lux > freeCollateralLux) {
        setWarn("Need ", formatDusk(lux), " · have ", formatDusk(freeCollateralLux));
        return;
      }
      const applied = applyBuyReserves(yesReserve, noReserve, outcome, lux);
      if (applied == null) {
        setWarn("Too large or too small for pool");
        return;
      }
      const { shares } = applied;
      const avg = avgEffectivePrice(lux, shares);
      const impact = priceImpactBps(yesReserve, noReserve, outcome, true, lux);
      const rows = [
        addQuoteRow(
          "Avg price",
          avg != null ? `${(avg * 100).toFixed(1)}¢` : "-"
        ),
        addQuoteRow("If win", formatDusk(shares), tone),
        addQuoteRow(
          "Price impact",
          impact != null ? `${(impact / 100).toFixed(2)}%` : "-"
        ),
      ];
      if (freeCollateralLux != null) {
        const left = freeCollateralLux - lux;
        rows.push(
          addQuoteRow(
            "Free after",
            left >= 0n ? formatDusk(left) : "insufficient"
          )
        );
      }
      setQuoteGrid(
        "shares estimated",
        `${shares.toLocaleString()} ${outcomeLabel}`,
        rows
      );
    } else {
      const shares = sharesFromInput();
      if (shares == null) {
        setMuted("Enter shares");
        return;
      }
      const applied = applySellReserves(yesReserve, noReserve, outcome, shares);
      if (applied == null) {
        setWarn("Too large or too small for pool");
        return;
      }
      const { collateral: out } = applied;
      const avg = avgEffectivePrice(out, shares);
      const impact = priceImpactBps(yesReserve, noReserve, outcome, false, shares);
      setQuoteGrid("from sell (est.)", formatDusk(out), [
        addQuoteRow(
          "Avg price",
          avg != null ? `${(avg * 100).toFixed(1)}¢` : "-"
        ),
        addQuoteRow(
          "Price impact",
          impact != null ? `${(impact / 100).toFixed(2)}%` : "-"
        ),
        addQuoteRow("Shares in", `${shares.toLocaleString()} ${outcomeLabel}`),
      ]);
    }
  }

  function paintToggles() {
    sideRow.replaceChildren();
    for (const [id, label] of [
      ["buy", "Buy"],
      ["sell", "Sell"],
    ]) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `trade-side-pill${side === id ? " is-active" : ""}${id === "sell" ? " is-sell" : ""}`;
      btn.textContent = label;
      btn.disabled = closed;
      btn.addEventListener("click", () => {
        side = id;
        paintToggles();
        paintAmount();
        updateQuote();
      });
      sideRow.append(btn);
    }

    function outcomeBtn(out, label, pct) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `trade-outcome-btn${outcome === out ? " is-selected" : ""}${out === 1 ? " is-no" : " is-yes"}`;
      btn.disabled = closed;
      btn.innerHTML = `<span class="trade-outcome-name">${label}</span><span class="trade-outcome-pct">${formatPriceCents(pct)}</span>`;
      btn.addEventListener("click", () => {
        outcome = out;
        paintToggles();
        paintAmount();
        updateQuote();
      });
      return btn;
    }
    outcomeRow.replaceChildren(
      outcomeBtn(0, "YES", spotPct.pYes),
      outcomeBtn(1, "NO", spotPct.pNo)
    );
  }

  async function submitTrade() {
    if (busy) return;
    if (!isParticipantReady()) {
      throw new Error("Finish participant onboarding first (wallet icon)");
    }
    const key = loadTradingKey();
    if (!key?.sk_hex) throw new Error("Connect a trading key in Account settings");
    const participant = loadParticipant();
    if (
      participant?.trading_commitment_hex &&
      key.commitment_hex &&
      participant.trading_commitment_hex.toLowerCase() !== key.commitment_hex.toLowerCase()
    ) {
      throw new Error("Trading key does not match linked participant commitment");
    }

    let amount;
    let sharesOrOut = null;
    if (side === "buy") {
      const lux = collateralLuxFromInput();
      if (lux == null) throw new Error("amount must be > 0");
      amount = Number(lux);
    } else {
      const shares = sharesFromInput();
      if (shares == null) throw new Error("shares must be > 0");
      amount = Number(shares);
    }
    if (!amount || amount <= 0) throw new Error("amount must be > 0");
    // yesReserve/noReserve come from an on-chain market_info read
    // (market-detail.js), not gateway data - but that read can fail
    // (node unreachable, chain error), in which case they're null here.
    // buySharesOut/sellCollateralOut do BigInt(reserve) unconditionally
    // and throw a raw "Cannot convert null to a BigInt" if so; the quote
    // preview already guards this (line ~436) but submit didn't, so a
    // trade could be attempted against nonexistent reserve data with a
    // confusing low-level error instead of a clear one.
    if (yesReserve == null || noReserve == null) {
      throw new Error("Pool reserves unavailable (on-chain read failed) - refresh and try again.");
    }

    await refreshCollateral();
    if (side === "buy") {
      const need = BigInt(amount);
      if (freeCollateralLux == null) throw new Error("Could not read free collateral");
      if (need > freeCollateralLux) {
        throw new Error(
          `Insufficient free collateral: need ${formatDuskText(need)}, have ${formatDuskText(freeCollateralLux)}`
        );
      }
      sharesOrOut = buySharesOut(yesReserve, noReserve, outcome, need);
      if (sharesOrOut == null) {
        throw new Error("This size would drain the pool or is too small");
      }
    } else {
      sharesOrOut = sellCollateralOut(yesReserve, noReserve, outcome, BigInt(amount));
      if (sharesOrOut == null) {
        throw new Error("This size would drain the pool or is too small");
      }
    }

    const sideLabel = side === "buy" ? "Buy" : "Sell";
    const outcomeLabel = outcome === 0 ? "YES" : "NO";
    const ui = showGlobalTx({
      label: `${sideLabel} ${outcomeLabel} · #${marketId}`,
    });

    busy = true;
    paintActions();
    try {
      const dusk = await getChainApp();
      const commitment = Array.from(hexToBytes(key.commitment_hex));
      const readChainNonce = async () =>
        Number(
          await dusk.readContract({
            contract: "predictionMarket",
            functionName: "next_nonce",
            args: commitment,
          })
        );

      // Nonce is reserved through the shared per-commitment queue
      // (trader-nonce.js) so a trade on this market and, say, a claim on
      // another market submitted moments apart from this same page don't
      // race on the same chain-read nonce. If a submit still fails on a
      // nonce error - a genuine cross-tab/device race the in-page queue
      // can't see - it's retried once with a freshly reserved nonce, but
      // only if the user opted into that in Settings (trade-settings.js)
      // - see docs/prediction-market/prediction-market-portal-fixes-plan.md §1.
      async function attemptTrade(nonce) {
        const payload = encodeTradePayload(marketId, outcome, side === "buy", amount);
        const { signatureHex } = await signTraderAuth({
          skHex: key.sk_hex,
          op: OP.TRADE,
          commitmentHex: key.commitment_hex,
          nonce,
          payloadBytes: payload,
        });
        const auth = buildTraderAuthArg({
          pkBase58: key.pk_base58,
          nonce,
          signatureHex,
        });
        const tx = await dusk.writeContract({
          contract: "predictionMarket",
          functionName: "trade",
          args: {
            auth,
            market_id: marketId,
            outcome,
            is_buy: side === "buy",
            amount,
          },
          privacy: "public",
          // Trade spends free collateral already inside the contract - do NOT
          // attach `deposit`/`amount` or the wallet would move extra Moonlight DUSK.
          // `display` is opaque metadata for the wallet approval UI only.
          display: {
            summary:
              side === "buy"
                ? `${sideLabel} ${outcomeLabel} · spend ${formatDuskText(BigInt(amount))} free collateral`
                : `${sideLabel} ${outcomeLabel} · ${amount.toLocaleString()} shares`,
            marketId,
            side,
            outcome: outcomeLabel,
            amountLux: String(amount),
            amountDusk: side === "buy" ? luxToDusk(amount) : null,
            expectedOut:
              sharesOrOut != null
                ? side === "buy"
                  ? `${sharesOrOut.toLocaleString()} shares`
                  : formatDuskText(sharesOrOut)
                : null,
            note: "Wallet pays gas only. Buy spend comes from deposited free collateral.",
          },
        });
        return trackTx(tx, { ui, autoDismissMs: 8000 });
      }

      const tracked = await withNonceRetry(key.commitment_hex, readChainNonce, attemptTrade, {
        retryOnStaleNonce: isAutoRetryEnabled(),
        onRetrying: (e) => {
          ui.setStatus(
            "retrying",
            "That nonce was stale (another action beat it to the chain) - retrying with a fresh one…"
          );
          log?.(`trade rejected on a stale nonce (${e.message}) - retrying once with a fresh one`);
        },
      });

      await refreshCollateral();
      await refreshPosition();
      log?.(`trade tx ${tracked.hash} executed (participant ${participant.participant_id})`);
      onDone?.({
        hash: tracked.hash,
        side,
        outcome,
        amount,
        sharesOrOut: sharesOrOut != null ? Number(sharesOrOut) : null,
        traderCommitment: key.commitment_hex,
        isBuy: side === "buy",
      });
    } catch (e) {
      ui.setStatus("failed", e.message || String(e));
      throw e;
    } finally {
      busy = false;
      paintActions();
    }
  }

  function resetTrade() {
    side = "buy";
    outcome = 0;
    amountUnit = "dusk";
    paintToggles();
    paintAmount();
    if (amountInput) amountInput.value = "0.1";
    updateQuote();
    // Clear any inline error banners in the ticket.
    root.querySelectorAll(".error-banner").forEach((el) => el.remove());
  }

  function confirmRow(label, value, opts = {}) {
    const row = document.createElement("div");
    row.className = `confirm-sheet-row${opts.emphasis ? " is-emphasis" : ""}${opts.tone ? ` is-${opts.tone}` : ""}`;
    const l = document.createElement("span");
    l.className = "confirm-sheet-label";
    l.textContent = label;
    const v = document.createElement("span");
    v.className = "confirm-sheet-value";
    v.append(value);
    row.append(l, v);
    return row;
  }

  function buildConfirmSheet() {
    const sideLabel = side === "buy" ? "Buy" : "Sell";
    const outcomeLabel = outcome === 0 ? "YES" : "NO";
    const tone = outcome === 0 ? "yes" : "no";
    const sheet = document.createElement("div");
    sheet.className = `confirm-sheet confirm-sheet--${tone}`;

    const hero = document.createElement("div");
    hero.className = "confirm-sheet-hero";
    const chip = document.createElement("div");
    chip.className = `confirm-sheet-chip confirm-sheet-chip--${tone}`;
    chip.innerHTML = `<span class="confirm-sheet-chip-side">${sideLabel}</span><span class="confirm-sheet-chip-outcome">${outcomeLabel}</span>`;
    const marketLine = document.createElement("p");
    marketLine.className = "confirm-sheet-market";
    marketLine.textContent = `Event #${marketId}`;
    hero.append(chip, marketLine);
    sheet.append(hero);

    const rows = document.createElement("div");
    rows.className = "confirm-sheet-rows";

    const spot =
      outcome === 0
        ? spotPct.pYes != null
          ? formatPriceCents(spotPct.pYes)
          : "-"
        : spotPct.pNo != null
          ? formatPriceCents(spotPct.pNo)
          : "-";
    rows.append(confirmRow("Spot", `${outcomeLabel} ${spot}`));

    if (side === "buy") {
      const lux = collateralLuxFromInput();
      const shares =
        lux != null ? buySharesOut(yesReserve, noReserve, outcome, lux) : null;
      const spend =
        lux != null
          ? amountUnit === "lux"
            ? `${lux.toLocaleString()} LUX`
            : formatDusk(lux)
          : `${amountInput?.value || "-"} ${amountUnit.toUpperCase()}`;
      rows.append(
        confirmRow("You spend", spend, { emphasis: true }),
        confirmRow(
          "You receive (est.)",
          shares != null ? `${shares.toLocaleString()} ${outcomeLabel} shares` : "-",
          { emphasis: true, tone }
        )
      );
      if (lux != null && shares != null) {
        const avg = avgEffectivePrice(lux, shares);
        const impact = priceImpactBps(yesReserve, noReserve, outcome, true, lux);
        rows.append(
          confirmRow(
            "Avg price",
            avg != null ? `${(avg * 100).toFixed(1)}¢` : "-"
          ),
          confirmRow("If win", formatDusk(shares), { tone }),
          confirmRow(
            "Price impact",
            impact != null ? `${(impact / 100).toFixed(2)}%` : "-"
          )
        );
      }
      if (lux != null && freeCollateralLux != null) {
        const left = freeCollateralLux - lux;
        rows.append(
          confirmRow(
            "Free after",
            left >= 0n ? formatDusk(left) : "insufficient"
          )
        );
      }
    } else {
      const shares = sharesFromInput();
      const out =
        shares != null
          ? sellCollateralOut(yesReserve, noReserve, outcome, shares)
          : null;
      rows.append(
        confirmRow(
          "You sell",
          shares != null
            ? `${shares.toLocaleString()} ${outcomeLabel} shares`
            : `${amountInput?.value || "-"} shares`,
          { emphasis: true, tone }
        ),
        confirmRow(
          "You receive (est.)",
          out != null ? formatDusk(out) : "-",
          { emphasis: true }
        )
      );
      if (shares != null && out != null) {
        const avg = avgEffectivePrice(out, shares);
        const impact = priceImpactBps(
          yesReserve,
          noReserve,
          outcome,
          false,
          shares
        );
        rows.append(
          confirmRow(
            "Avg price",
            avg != null ? `${(avg * 100).toFixed(1)}¢` : "-"
          ),
          confirmRow(
            "Price impact",
            impact != null ? `${(impact / 100).toFixed(2)}%` : "-"
          )
        );
      }
    }

    sheet.append(rows);

    const notes = document.createElement("ul");
    notes.className = "confirm-sheet-notes";
    const noteItems =
      side === "buy"
        ? [
            "Wallet pays gas only - buy spend comes from deposited free collateral.",
            "Share estimate uses current pool reserves; final fill is on-chain.",
            TRADE_PRIVACY_BLURB,
          ]
        : [
            "Wallet pays gas only - sell proceeds credit free collateral in-contract.",
            "Collateral estimate uses current pool reserves; final fill is on-chain.",
            TRADE_PRIVACY_BLURB,
          ];
    for (const text of noteItems) {
      const li = document.createElement("li");
      li.textContent = text;
      notes.append(li);
    }
    sheet.append(notes);

    return { sheet, sideLabel, outcomeLabel, tone };
  }

  function paintActions() {
    const submit = Button({
      label: busy ? "Submitting…" : side === "buy" ? "Place trade" : "Sell shares",
      variant: "primary",
      disabled: closed || busy,
      onClick: () => {
        const { sheet, sideLabel, outcomeLabel, tone } = buildConfirmSheet();
        const actions = document.createElement("div");
        actions.className = "confirm-sheet-actions";
        let dialog;
        const confirm = Button({
          label: `Confirm ${sideLabel} ${outcomeLabel}`,
          variant: tone === "no" || side === "sell" ? "danger" : "primary",
          onClick: async () => {
            dialog?.close();
            try {
              await submitTrade();
            } catch (e) {
              log?.(`trade failed: ${e.message || e}`);
              root.prepend(ErrorBanner({ message: e.message || String(e) }));
            }
          },
        });
        confirm.classList.add("confirm-sheet-submit");
        actions.append(
          confirm,
          Button({
            label: "Cancel",
            onClick: () => dialog?.close(),
          })
        );
        sheet.append(actions);
        dialog = Modal({
          title: "Review trade",
          content: sheet,
          onClose: () => {},
        });
        dialog.classList.add("modal--trade-confirm");
      },
    });
    submit.classList.add("trade-submit-btn", "dt-btn", "dt-btn-primary");

    const bar = ActionBar([
      submit,
      Button({
        label: "Reset",
        disabled: busy,
        onClick: () => resetTrade(),
      }),
    ]);
    bar.classList.add("trade-actions");
    actionsHost.replaceChildren(bar);
  }

  paintToggles();
  paintAmount();
  updateQuote();
  paintActions();
  refreshCollateral();
  refreshPosition();

  /**
   * Pushes fresh market data (reserves/price/status/close info) into an
   * already-mounted ticket without remounting it - remounting would blow
   * away whatever the user has typed into the amount field or which
   * side/outcome they've picked, the same class of bug fixed in
   * browse-page.js's poll wiring. Only the fields present in `next` are
   * updated; omit a field to leave it as-is. Attached to the returned
   * root node (see market-detail.js's live-poll wiring) rather than
   * changing this function's return shape, so existing `.append(
   * renderTradeTicket(...))` call sites need no changes.
   */
  function updateMarketData(next = {}) {
    if (next.yesPercent !== undefined) yesPercent = next.yesPercent;
    if (next.yesReserve !== undefined) yesReserve = next.yesReserve;
    if (next.noReserve !== undefined) noReserve = next.noReserve;
    if (next.marketStatus !== undefined) marketStatus = next.marketStatus;
    if (next.closeBlock !== undefined) closeBlock = next.closeBlock;
    if (next.blockHeight !== undefined) blockHeight = next.blockHeight;
    spotPct = computeSpotPct();
    closed = computeClosed();
    paintClosedBanner();
    paintToggles();
    paintAmount();
    updateQuote();
    paintActions();
  }
  root.updateMarketData = updateMarketData;

  return root;
}
